import type { SaunaSlug } from "@/data/sauna-services";
import { Dialog, DialogContent, DialogDescription, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

const photos = {
  outdoorCabin: { file: "outdoor-infrared-cabin.webp", caption: "Outdoor infrared sauna — cabin design" },
  outdoorPlan: { file: "outdoor-infrared-plan.webp", caption: "Outdoor infrared sauna — seating layout" },
  outdoorGarden: { file: "outdoor-infrared-garden.webp", caption: "Outdoor infrared sauna — garden inspiration" },
  cabinTop: { file: "sauna-cabin-3040a-hem-cl-top-shot.webp", caption: "Sauna interior — overhead layout" },
  cabinAClosed: { file: "sauna-cabin-3040a-sp-br-front-left-side-1.webp", caption: "Timber-front cabin — angled view" },
  cabinALit: { file: "sauna-cabin-3040a-sp-cl-front-left-side.webp", caption: "Timber-front cabin — illuminated interior" },
  cabinBAngle: { file: "sauna-cabin-3040b-hem-cl-front-left-side.webp", caption: "Glass-front cabin — angled view" },
  cabinBFront: { file: "sauna-cabin-3040b-sp-br-front.webp", caption: "Glass-front cabin — front view" },
  cabinBLit: { file: "sauna-cabin-3040b-sp-cl-front.webp", caption: "Glass-front cabin — seating and lighting" },
  cabinCFront: { file: "sauna-cabin-3040c-sp-cl-front.webp", caption: "Panoramic glass-front cabin" },
  delivery: { file: "sauna-supply-delivery.webp", caption: "Sauna equipment secured for delivery" },
  fitting: { file: "sauna-fitting.webp", caption: "Engineer fitting the sauna entrance" },
  assembly: { file: "sauna-assembly.webp", caption: "Sauna cabin and timber panel assembly" },
  panels: { file: "sauna-panel-assembly.webp", caption: "Engineers assembling a timber sauna panel" },
  interior: { file: "sauna-detail-1.webp", caption: "Timber benches and integrated lighting" },
  heater: { file: "sauna-heater.webp", caption: "Sauna heater and stone arrangement" },
  equipment: { file: "sauna-detail-2.webp", caption: "Sauna heating equipment and protective guards" },
  controls: { file: "sauna-detail-3.webp", caption: "Temperature controller and electrical control enclosure" },
  lighting: { file: "sauna-detail-4.webp", caption: "Sauna seating, ventilation grilles and ceiling lighting" },
  entrance: { file: "sauna-detail-5.webp", caption: "Sauna entrance, controls and safety signage" },
};

const selections: Record<SaunaSlug, (keyof typeof photos)[]> = {
  "advise-design": ["interior", "lighting", "entrance"],
  supply: ["delivery", "heater", "equipment", "controls"],
  install: ["fitting", "assembly", "panels", "interior", "lighting", "heater", "equipment", "controls", "entrance"],
  "maintenance-repair": ["heater", "equipment", "controls", "entrance"],
};

export function SaunaGallery({ slug, designs = false, outdoorInfrared = false }: { slug: SaunaSlug; designs?: boolean; outdoorInfrared?: boolean }) {
  const productImages = designs || outdoorInfrared;
  const title = outdoorInfrared ? "Outdoor Infrared Sauna" : designs ? "Explore sauna cabin designs" : "Sauna details";
  const selected: (keyof typeof photos)[] = outdoorInfrared
    ? ["outdoorCabin", "outdoorPlan", "outdoorGarden"]
    : designs
    ? ["cabinTop", "cabinAClosed", "cabinALit", "cabinBAngle", "cabinBFront", "cabinBLit", "cabinCFront"]
    : selections[slug];
  return (
    <section id={outdoorInfrared ? "outdoor-infrared-gallery" : designs ? "sauna-cabin-gallery" : undefined} className="py-16 bg-secondary/40 scroll-mt-28" aria-label={title}>
      <div className="container mx-auto px-4 max-w-7xl">
        <h2 className="text-3xl font-bold font-display mb-3">{title}</h2>
        <p className="text-muted-foreground mb-8">{outdoorInfrared ? "Explore the cabin design, seating layout and a garden setting. These images illustrate design ideas; discuss your preferred size, specification and outdoor installation requirements with our team." : designs ? "Explore timber and glass-front cabin styles, seating layouts and lighting details. Select an image for a closer look, and discuss your preferred design and custom dimensions with our team." : "Explore the interiors, equipment and finishing details. Select a photo to view it in full."}</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {selected.map(key => {
            const photo = photos[key];
            const src = `${import.meta.env.BASE_URL}images/${photo.file}`;
            return (
              <figure key={key} className="overflow-hidden rounded-2xl border border-border bg-card">
                <Dialog>
                  <DialogTrigger asChild>
                    <button type="button" aria-label={`View full photo: ${photo.caption}`} className="block w-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary">
                      <img src={src} alt={photo.caption} loading="lazy" width={productImages ? 1024 : 768} height={productImages ? 768 : 1024} className={productImages ? "w-full h-72 object-contain p-3 bg-white" : "w-full h-72 object-cover"} />
                    </button>
                  </DialogTrigger>
                  <DialogContent className="max-w-4xl">
                    <DialogTitle className="pr-6">{photo.caption}</DialogTitle>
                    <DialogDescription>Sauna photo gallery</DialogDescription>
                    <img src={src} alt={photo.caption} className="max-h-[70vh] w-full object-contain" />
                  </DialogContent>
                </Dialog>
                <figcaption className="p-4 text-sm font-medium">{photo.caption}</figcaption>
              </figure>
            );
          })}
        </div>
      </div>
    </section>
  );
}
